/**
 * Lớp style + helper dùng chung cho khu Vận khí.
 *
 * Giống `boi-de/topic-style.ts`: class Tailwind phải là literal đầy đủ, KHÔNG ghép
 * động (`text-${x}-500` sẽ bị JIT bỏ qua khi build).
 */
import {
  AlertTriangle,
  Angry,
  Ban,
  BookOpen,
  Brain,
  BrainCog,
  CalendarX,
  Car,
  CloudOff,
  CloudRain,
  Coffee,
  Coins,
  EyeOff,
  FileSignature,
  FileX,
  Gift,
  Handshake,
  Heart,
  HeartCrack,
  HeartHandshake,
  Home,
  Hourglass,
  House,
  Layers,
  LayoutList,
  Leaf,
  ListChecks,
  Luggage,
  MapPinned,
  Megaphone,
  MessageCircleOff,
  MessageSquareWarning,
  MessagesSquare,
  Network,
  OctagonX,
  PartyPopper,
  Plane,
  Presentation,
  RotateCcw,
  Scale,
  SearchX,
  ShieldAlert,
  Shuffle,
  Snowflake,
  Sparkles,
  Sun,
  Swords,
  Target,
  Theater,
  Thermometer,
  TrafficCone,
  TreePalm,
  Truck,
  UserCheck,
  Users,
  Wallet,
  Wand2,
  Wine,
  type LucideIcon,
} from "lucide-react";

import type { VanKhiKind, VanKhiMucDo } from "@/lib/boide-api";

/** Tên icon trong `van_khi_label_data.py` → component lucide. */
export const VAN_KHI_LABEL_ICONS: Record<string, LucideIcon> = {
  AlertTriangle,
  Angry,
  Ban,
  BookOpen,
  Brain,
  BrainCog,
  CalendarX,
  Car,
  CloudOff,
  CloudRain,
  Coffee,
  Coins,
  EyeOff,
  FileSignature,
  FileX,
  Gift,
  Handshake,
  Heart,
  HeartCrack,
  HeartHandshake,
  Home,
  Hourglass,
  House,
  Layers,
  LayoutList,
  Leaf,
  ListChecks,
  Luggage,
  MapPinned,
  Megaphone,
  MessageCircleOff,
  MessageSquareWarning,
  MessagesSquare,
  Network,
  OctagonX,
  PartyPopper,
  Plane,
  Presentation,
  RotateCcw,
  Scale,
  SearchX,
  ShieldAlert,
  Shuffle,
  Snowflake,
  Sparkles,
  Sun,
  Swords,
  Target,
  Theater,
  Thermometer,
  TrafficCone,
  TreePalm,
  Truck,
  UserCheck,
  Users,
  Wallet,
  Wand2,
  Wine,
};

export function labelIcon(iconName: string): LucideIcon {
  return VAN_KHI_LABEL_ICONS[iconName] ?? Sparkles;
}

export const KIND_META: Record<
  VanKhiKind,
  { tab: string; noun: string; window: string; advice: string }
> = {
  day: {
    tab: "Hôm nay",
    noun: "Vận ngày",
    window: "Hôm nay xem miễn phí. Ngày khác (trước hoặc sau) mở bằng số dư.",
    advice: "Hôm nay bạn nên làm gì?",
  },
  week: {
    tab: "Tuần này",
    noun: "Vận tuần",
    window: "Tuần này (Thứ Hai → Chủ Nhật) xem miễn phí. Tuần khác mở bằng số dư.",
    advice: "Tuần này bạn nên làm gì?",
  },
  month: {
    tab: "Tháng này",
    noun: "Vận tháng",
    window: "Tháng âm lịch hiện tại xem miễn phí. Tháng khác mở bằng số dư.",
    advice: "Tháng này bạn nên làm gì?",
  },
};

export const MUC_DO_META: Record<VanKhiMucDo, { label: string; text: string; bar: string }> = {
  cao: { label: "Thuận", text: "text-element-wood", bar: "bg-element-wood" },
  vua: { label: "Vừa", text: "text-primary", bar: "bg-primary" },
  thap: { label: "Kém", text: "text-destructive", bar: "bg-destructive" },
};

/** Điểm ngày (0–100) của ngay_tot_engine → mức độ để tô màu, cùng ngưỡng với prompt. */
export function scoreToMucDo(score: number): VanKhiMucDo {
  if (score >= 70) return "cao";
  if (score >= 45) return "vua";
  return "thap";
}

/** Màu vòng điểm may mắn theo dải điểm (literal class, không ghép động). */
export function scoreRingClass(score: number): string {
  if (score >= 80) return "text-element-wood";
  if (score >= 62) return "text-primary";
  if (score >= 48) return "text-element-earth";
  return "text-destructive";
}

export type Countdown = { days: number; hours: number; minutes: number; seconds: number };

export function splitCountdown(ms: number): Countdown {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** dd/mm — dùng cho nhãn ngắn trên chip và badge. */
export function shortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return d && m ? `${d}/${m}` : iso;
}
